/**
 * Video Material Behavior
 * 
 * Applies video texture to mesh material's emissive channel.
 * Uses shared VideoTextureUtils for reusable video texture handling.
 * 
 * Blender Setup:
 *   hm_behavior_id: 'material:video'
 *   hm_behavior_src: 'videos/my_video.mp4'
 *   hm_behavior_autoplay: true
 *   hm_behavior_loop: true
 *   hm_behavior_emissive_intensity: 1.5
 */

import type { AbstractMesh } from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior } from '../../core/BehaviorRegistry';
import { logger } from '../../core/Logger';
import { 
  applyVideoMaterialToMesh, 
  cleanupVideoFromMesh, 
  playVideoOnMesh,
  type VideoMaterialConfig 
} from '../../assets/VideoTextureUtils';
import { registerMaterialBehavior, unregisterMaterialBehavior } from './MaterialBehaviorRegistry';

// Store video material data for registry
const videoMaterialsByEntity = new Map<string, {
  mesh: AbstractMesh;
  params: VideoMaterialParams;
}>();

/**
 * Video material behavior parameters (matches VideoMaterialConfig)
 */
export interface VideoMaterialParams {
  /** Video file path relative to public/ */
  src: string;
  /** Auto-play on spawn (default: true) */
  autoplay: boolean;
  /** Loop video playback (default: true) */
  loop: boolean;
  /** Emissive intensity multiplier (default: 1.0) */
  emissive_intensity: number;
}

function extractVideoParams(mesh: AbstractMesh): VideoMaterialParams {
  const extras = mesh.metadata?.gltf?.extras || mesh.metadata || {};
  
  return {
    src: extras.hm_behavior_src || '',
    autoplay: extras.hm_behavior_autoplay !== false,
    loop: extras.hm_behavior_loop !== false,
    emissive_intensity: extras.hm_behavior_emissive_intensity ?? 1.0,
  };
}

export const videoMaterialBehavior: EntityBehavior = {
  id: 'material:video',
  displayName: 'Video Material',
  type: EntityType.DYNAMIC_OBJECT,
  category: 'object',
  tags: ['material', 'video', 'emissive'],
  
  onSpawn: (api) => {
    const mesh = api.self.mesh;
    if (!mesh) {
      logger.assets.warn(`video_material behavior: No mesh for entity ${api.self.id}`);
      return;
    }
    
    const params = extractVideoParams(mesh);
    
    if (!params.src) {
      logger.assets.warn(`video_material behavior: Missing src parameter for ${api.self.id}`);
      return;
    }
    
    logger.assets.debug(`video_material: Applying video "${params.src}" to ${mesh.name}`);
    
    // Use shared utility to apply video material
    const config: VideoMaterialConfig = {
      src: params.src,
      autoplay: params.autoplay,
      loop: params.loop,
      emissiveIntensity: params.emissive_intensity,
    };
    
    const videoTexture = applyVideoMaterialToMesh(mesh, config);
    
    // Attempt autoplay (may be blocked by browser)
    if (videoTexture && params.autoplay) {
      playVideoOnMesh(mesh);
    }
    
    // Store for registry
    videoMaterialsByEntity.set(api.self.id, { mesh, params });
    
    // Register with central registry for debug UI
    registerMaterialBehavior({
      entityId: api.self.id,
      mesh,
      behaviorId: 'material:video',
      displayName: 'Video Material',
      getParams: (): Record<string, number | string | boolean> => {
        const entry = videoMaterialsByEntity.get(api.self.id);
        if (!entry) return {};
        return {
          src: entry.params.src,
          autoplay: entry.params.autoplay,
          loop: entry.params.loop,
          emissive_intensity: entry.params.emissive_intensity,
        };
      },
      setParam: (name, value) => {
        // Video params are mostly read-only at runtime
        // Could extend to support play/pause/seek later
        logger.assets.debug(`video_material: setParam ${name}=${value} not yet supported`);
      },
    });
  },
  
  onDestroy: (api) => {
    unregisterMaterialBehavior(api.self.id);
    videoMaterialsByEntity.delete(api.self.id);
    
    const mesh = api.self.mesh;
    if (!mesh) return;
    
    cleanupVideoFromMesh(mesh);
    logger.assets.debug(`video_material: Cleaned up ${api.self.id}`);
  },
};
